import { useMemo, useState } from "react";
import type { KeyboardEvent } from "react";
import { useSelector } from "react-redux";
import { Button, Dropdown, Empty, Popover, Select, Spin, Tag } from "antd";
import { CheckCircleFilled, MoreOutlined } from "@ant-design/icons";
import { useHeadshot } from "@hooks/useHeadshot";
import {
	PLAN_STATUS_ORDER,
	currentPhaseIndex,
	getInitials,
	phaseCardDetails,
	phaseElapsedShare,
	planCardDates,
	planCardInjury,
	planCardPhase,
	planCardStaff,
} from "@utils/rtp_utils";
import { useRtpPlans } from "./rtpHooks";
import { CreatePlanWizard } from "./CreatePlanWizard";
import type { RtpPhaseSummary, RtpPlanListItem, RtpPlanStatus } from "./types";

// Same order as the cards.
const STATUS_OPTIONS: { value: RtpPlanStatus; label: string }[] = [
	{ value: "active", label: "Active" },
	{ value: "draft", label: "Draft" },
	{ value: "completed", label: "Completed" },
	{ value: "cancelled", label: "Cancelled" },
];

// Cancelled plans are hidden until the user turns that status on.
const DEFAULT_STATUSES: RtpPlanStatus[] = ["active", "draft", "completed"];

interface PlansListProps {
	onOpenPlan: (id: number | null) => void;
}

interface TeamPlayer {
	player_id: number;
	first_name: string;
	last_name: string;
	number?: number | string | null;
	position?: string | null;
	headshot_image_url?: string | null;
}

// Popover title: a green tick for a completed phase, a blue dot for the current one.
const PhaseTitle = ({ phase, index, isCurrent }: { phase: RtpPhaseSummary; index: number; isCurrent: boolean }) => (
	<span className="rtp-plan-phase-pop-title">
		{phase.completedAt ? <CheckCircleFilled className="rtp-plan-phase-pop-done" /> : null}
		{isCurrent ? <span className="rtp-plan-phase-pop-now" /> : null}
		Phase {index + 1} · {phase.name}
	</span>
);

// One tile per plan. The bar has one segment per phase; hover opens a popover with the phase
// dates.
const PlanCard = ({
	plan,
	player,
	staff,
	onOpen,
	onDuplicate,
}: {
	plan: RtpPlanListItem;
	player?: TeamPlayer;
	staff: string;
	onOpen: () => void;
	onDuplicate: () => void;
}) => {
	const headshot = useHeadshot(player);
	const current = currentPhaseIndex(plan);
	const playerMeta = [player?.number != null && player.number !== "" ? `#${player.number}` : "", player?.position ?? ""]
		.filter(Boolean)
		.join(" · ");
	const openOnKey = (event: KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") onOpen();
	};

	return (
		<div
			className="rtp-plan-card"
			role="button"
			tabIndex={0}
			data-attr="settings.rtp-plans.open"
			onClick={onOpen}
			onKeyDown={openOnKey}
		>
			<div className="rtp-plan-card-head">
				{headshot ? (
					<img className="rtp-plan-card-shot" src={headshot} alt="" />
				) : (
					<div className="rtp-plan-card-shot">{getInitials(plan.playerName)}</div>
				)}
				<div className="rtp-plan-card-who">
					<div className="rtp-plan-card-name">{plan.playerName}</div>
					{playerMeta ? <div className="rtp-plan-card-meta">{playerMeta}</div> : null}
				</div>
				<span className={`rtp-status-pill rtp-status-pill--${plan.status}`}>{plan.status}</span>
				{/* A click on the card opens the plan, so the menu only duplicates it. React passes events
				    from the portalled menu up to the card, so every handler here stops them, or a menu
				    click would also open the plan. */}
				<Dropdown
					trigger={["click"]}
					placement="bottomRight"
					menu={{
						items: [
							{
								key: "duplicate",
								label: <span data-attr="settings.rtp-plans.card-duplicate">Duplicate plan</span>,
							},
						],
						onClick: ({ domEvent }) => {
							domEvent.stopPropagation();
							onDuplicate();
						},
					}}
				>
					<Button
						type="text"
						size="small"
						className="rtp-plan-card-menu"
						icon={<MoreOutlined />}
						aria-label="Plan actions"
						data-attr="settings.rtp-plans.card-menu"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					/>
				</Dropdown>
			</div>
			<dl className="rtp-plan-card-fields">
				<dt>Plan</dt>
				<dd>{plan.name}</dd>
				<dt>Injury</dt>
				<dd>{planCardInjury(plan)}</dd>
				<dt>Timeline</dt>
				<dd>{planCardDates(plan)}</dd>
				<dt>Phase</dt>
				<dd>{planCardPhase(plan)}</dd>
				<dt>Staff</dt>
				<dd>{staff || "—"}</dd>
			</dl>
			{/* One segment per phase, as wide as its days. */}
			<div className="rtp-plan-bar">
				{plan.phases.map((phase, index) => {
					const state = phase.completedAt ? " is-done" : index === current ? " is-now" : "";
					return (
						<Popover
							key={phase.id}
							// The popover portals outside .rtp-scope; rtp-wz carries the design tokens there.
							rootClassName="rtp-wz"
							placement="bottom"
							title={<PhaseTitle phase={phase} index={index} isCurrent={index === current} />}
							content={phaseCardDetails(phase).range}
						>
							<span className={`rtp-plan-bar-seg${state}`} style={{ flexGrow: phaseCardDetails(phase).days }}>
								{index === current ? (
									<span className="rtp-plan-bar-fill" style={{ width: `${phaseElapsedShare(phase) * 100}%` }} />
								) : null}
							</span>
						</Popover>
					);
				})}
			</div>
		</div>
	);
};

export const PlansList = ({ onOpenPlan }: PlansListProps) => {
	const players = useSelector(({ team }: any) => team.players as TeamPlayer[]);
	const playerById = useMemo(() => new Map((players ?? []).map((p) => [p.player_id, p])), [players]);
	const filterByLabel = (input: string, option?: { label: string }) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	// The list loads every plan once and filters here: the player options must come from all
	// plans (not only the filtered ones), and the endpoint takes one status only.
	const { plans, loading } = useRtpPlans();
	const [playerId, setPlayerId] = useState<number | undefined>(undefined);
	// Several statuses at a time, like checkboxes.
	const [statuses, setStatuses] = useState<RtpPlanStatus[]>(DEFAULT_STATUSES);
	const toggleStatus = (value: RtpPlanStatus, checked: boolean) =>
		setStatuses((prev) => (checked ? [...prev, value] : prev.filter((s) => s !== value)));

	// Only the players who have a plan.
	const playerOptions = useMemo(
		() =>
			[...new Map(plans.map((plan) => [plan.playerId, plan.playerName])).entries()]
				.map(([value, label]) => ({ value, label }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[plans]
	);

	const [wizardOpen, setWizardOpen] = useState(false);
	// The plan to duplicate, or null for a new plan.
	const [wizardSourceId, setWizardSourceId] = useState<number | null>(null);
	const openWizard = (sourceId: number | null) => {
		setWizardSourceId(sourceId);
		setWizardOpen(true);
	};

	// Stable sort: inside a status group, plans keep the backend order (newest first).
	const sortedPlans = useMemo(
		() =>
			plans
				.filter((plan) => playerId == null || plan.playerId === playerId)
				.filter((plan) => statuses.includes(plan.status))
				.sort((a, b) => PLAN_STATUS_ORDER[a.status] - PLAN_STATUS_ORDER[b.status]),
		[plans, playerId, statuses]
	);

	return (
		<div>
			<div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
				<Select
					allowClear
					showSearch
					placeholder="Filter by player"
					style={{ minWidth: 320 }}
					options={playerOptions}
					value={playerId}
					onChange={setPlayerId}
					filterOption={filterByLabel}
				/>
				<div className="rtp-pills">
					{STATUS_OPTIONS.map((option) => (
						<Tag.CheckableTag
							key={option.value}
							checked={statuses.includes(option.value)}
							data-attr="settings.rtp-plans.filter-status"
							onChange={(checked) => toggleStatus(option.value, checked)}
						>
							{option.label}
						</Tag.CheckableTag>
					))}
				</div>
				<Button
					type="primary"
					style={{ marginLeft: "auto" }}
					data-attr="settings.rtp-plans.create"
					onClick={() => openWizard(null)}
				>
					New Plan
				</Button>
			</div>
			{loading ? (
				<div className="rtp-plan-grid-empty">
					<Spin />
				</div>
			) : sortedPlans.length === 0 ? (
				<Empty
					className="rtp-plan-grid-empty"
					description={plans.length === 0 ? "No plans yet." : "No plans match these filters."}
				/>
			) : (
				<div className="rtp-plan-grid">
					{sortedPlans.map((plan) => (
						<PlanCard
							key={plan.id}
							plan={plan}
							player={playerById.get(plan.playerId)}
							staff={planCardStaff(plan)}
							onOpen={() => onOpenPlan(plan.id)}
							onDuplicate={() => openWizard(plan.id)}
						/>
					))}
				</div>
			)}
			<CreatePlanWizard
				open={wizardOpen}
				sourcePlanId={wizardSourceId}
				onClose={() => setWizardOpen(false)}
				onOpenPlan={onOpenPlan}
			/>
		</div>
	);
};
